import { useCallback, useEffect, useState } from 'react';
import { getKycStatus } from '../api/kyc';
import { getMyPensionPlanTransfers } from '../api/pension-plan-transfers';
import getSignedTransferBulletins from '../api/kyc/getSignedTransferBulletins';
import { PensionPlanTransfer } from '../api/pension-plan-transfers/interfaces/PensionPlanTransfer';

export interface DatosResumenMulticuenta {
	cargando: boolean;
	kycStatus: string | null;
	/**
	 * 🔴 `/kyc/status` respondió. Si falla o no ha llegado, NO se afirma nada sobre la identidad
	 * (ni se pide verificarla ni se da por hecha): mismo criterio que Inicio.
	 */
	kycResuelto: boolean;
	transfers: PensionPlanTransfer[];
	signedBulletinIds: string[];
}

export interface ResultadoResumenMulticuenta extends DatosResumenMulticuenta {
	/** Vuelve a pedirlo todo (tras elegir destino o cerrar el alta de un plan). */
	recargar: () => void;
}

const VACIO: Omit<DatosResumenMulticuenta, 'cargando'> = {
	kycStatus: null,
	kycResuelto: false,
	transfers: [],
	signedBulletinIds: []
};

/**
 * Lo que el Resumen multicuenta pide por USUARIO (product#6, §3.2): KYC, traspasos y boletines
 * firmados, en paralelo y cada fallo por su lado (uno que falla no tumba los otros). Los planes no
 * se piden aquí: el Dashboard ya tiene `GET /users/plans`.
 *
 * Fase A1. Lo que se pide por PLAN (rentabilidad, aportación) llega con la A2.
 */
const useResumenMulticuenta = (token: string | null | undefined): ResultadoResumenMulticuenta => {
	const [datos, setDatos] = useState<DatosResumenMulticuenta>({ ...VACIO, cargando: !!token });
	const [vez, setVez] = useState(0);
	const recargar = useCallback(() => setVez((n) => n + 1), []);

	useEffect(() => {
		if (!token) return;
		let vivo = true;
		Promise.allSettled([
			getKycStatus(token),
			getMyPensionPlanTransfers(token),
			getSignedTransferBulletins(token)
		]).then(([kyc, traspasos, boletines]) => {
			if (!vivo) return;
			const kycData = kyc.status === 'fulfilled' && kyc.value.success ? kyc.value.data : undefined;
			setDatos({
				cargando: false,
				kycStatus: kycData?.kycStatus ?? null,
				kycResuelto: !!kycData,
				transfers:
					traspasos.status === 'fulfilled' && traspasos.value.success
						? traspasos.value.data ?? []
						: [],
				signedBulletinIds:
					boletines.status === 'fulfilled' ? boletines.value.data?.signedTransferIds ?? [] : []
			});
		});
		return () => {
			vivo = false;
		};
	}, [token, vez]);

	return { ...datos, recargar };
};

export default useResumenMulticuenta;
